/**
 * CheckoutSuccessPage — /checkout/success?session_id=…
 *
 * Polls GET /api/payments/status/{session_id} every 2 seconds (max 30s)
 * until the backend flips the transaction to `paid`, at which point the
 * cart is cleared server-side and we show an order confirmation.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ShoppingBag, Building2 } from "lucide-react";

const POLL_MS  = 2000;
const MAX_MS   = 45_000;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired | error
  const [data, setData] = useState(null);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    async function tick() {
      if (cancelled) return;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus(data.payment_status); return; }
      } catch (e) {
        setStatus("error"); return;
      }
      if (Date.now() - startedAt.current > MAX_MS) { setStatus("timeout"); return; }
      setTimeout(tick, POLL_MS);
    }
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="checkout-success-page">
      {status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin opacity-60 mb-3" />
          <h1 className="font-display text-3xl mb-1">Confirming your payment…</h1>
          <p className="opacity-70 text-sm">This usually takes a couple of seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto mb-3" style={{ color: "#16a34a" }} />
          <h1 className="font-display text-4xl mb-2" data-testid="payment-success-heading">Order confirmed</h1>
          <p className="opacity-80 mb-6">
            {data?.amount && `${data.currency?.toUpperCase()} ${data.amount.toFixed(2)} • `}
            Thank you — your order is on its way. A receipt will be emailed to you shortly.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Link to="/orders" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="view-orders">
              <ShoppingBag className="w-4 h-4" /> View my orders
            </Link>
            <Link to="/businesses" className="wofy-btn-ghost inline-flex items-center gap-1.5">
              <Building2 className="w-4 h-4" /> Keep shopping
            </Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <XCircle className="w-14 h-14 mx-auto mb-3 text-red-500" />
          <h1 className="font-display text-3xl mb-1">Payment {status}</h1>
          <p className="opacity-80 mb-4">Your payment did not go through. Your cart is still saved — try again whenever you're ready.</p>
          <Link to="/cart" className="wofy-btn-primary inline-flex">Back to cart</Link>
        </>
      )}
      {status === "timeout" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto opacity-60 mb-3" />
          <h1 className="font-display text-3xl mb-1">Still processing…</h1>
          <p className="opacity-80 mb-4">Your payment is taking a bit longer than usual. Check your orders in a few minutes — we'll flip it as soon as Stripe confirms.</p>
          <Link to="/orders" className="wofy-btn-primary inline-flex">View my orders</Link>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle className="w-14 h-14 mx-auto mb-3 text-red-500" />
          <h1 className="font-display text-3xl mb-1">Something went wrong</h1>
          <p className="opacity-80 mb-4">We couldn't verify your payment status. Check your orders to see if it went through.</p>
          <Link to="/orders" className="wofy-btn-primary inline-flex">View my orders</Link>
        </>
      )}
    </div>
  );
}
